import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { fmtDate } from "@/components/common";
import {
  Video, Plus, Calendar, Clock, Users, CheckCircle2, XCircle, UserCheck,
  Loader2, ChevronRight, Radio,
} from "lucide-react";

const STATUS_STYLES = {
  SCHEDULED: "bg-blue-500/10 text-blue-400 border-blue-500/30",
  COMPLETED: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30",
  CANCELLED: "bg-red-500/10 text-red-400 border-red-500/30",
  LIVE: "bg-purple-500/15 text-purple-300 border-purple-500/40",
};

const EMPTY = {
  name: "", description: "", date: "", start_time: "", end_time: "",
  host: "", join_url: "", meeting_id: "", passcode: "",
};

export default function Webinars() {
  const navigate = useNavigate();
  const [ov, setOv] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [o, w] = await Promise.all([api.get("/webinars/overview"), api.get("/webinars")]);
      setOv(o.data); setItems(w.data.items);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.name || !form.date) return toast.error("Webinar name and date are required");
    setBusy(true);
    try {
      const { data } = await api.post("/webinars", form);
      toast.success("Webinar created");
      setOpen(false); setForm(EMPTY);
      navigate(`/admin/webinars/${data.id}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const KPI = ({ label, value, icon: Icon, accent = "text-primary", testid }) => (
    <div data-testid={testid} className="glass rounded-xl p-4">
      <div className="flex items-center justify-between">
        <span className="text-xs uppercase tracking-wider text-muted-foreground">{label}</span>
        {Icon && <Icon className={`h-4 w-4 ${accent}`} />}
      </div>
      <p className="mt-2 text-2xl font-bold font-heading font-mono">{value ?? 0}</p>
    </div>
  );

  return (
    <div className="space-y-6 pb-10" data-testid="webinars-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold font-heading flex items-center gap-2"><Video className="h-6 w-6 text-primary" /> Webinar Management</h1>
          <p className="text-sm text-muted-foreground">Create webinars, register leads, drive attendance & convert with AI reminders.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-webinar-button" className="bg-primary text-primary-foreground"><Plus className="h-4 w-4 mr-1.5" /> New Webinar</Button>
          </DialogTrigger>
          <DialogContent className="bg-slate-950 border-slate-800 max-w-lg">
            <DialogHeader><DialogTitle>Create Webinar</DialogTitle></DialogHeader>
            <div className="space-y-3 max-h-[70vh] overflow-auto pr-1">
              <div><Label className="text-xs text-muted-foreground">Webinar name *</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="webinar-name" placeholder="Own Brand Loan Agency — Live Demo" className="mt-1 bg-slate-900/60 border-slate-700" /></div>
              <div><Label className="text-xs text-muted-foreground">Description</Label>
                <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="webinar-desc" rows={2} className="mt-1 bg-slate-900/60 border-slate-700" /></div>
              <div className="grid grid-cols-3 gap-3">
                <div><Label className="text-xs text-muted-foreground">Date *</Label>
                  <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="webinar-date" className="mt-1 bg-slate-900/60 border-slate-700" /></div>
                <div><Label className="text-xs text-muted-foreground">Start</Label>
                  <Input type="time" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} data-testid="webinar-start" className="mt-1 bg-slate-900/60 border-slate-700" /></div>
                <div><Label className="text-xs text-muted-foreground">End</Label>
                  <Input type="time" value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} className="mt-1 bg-slate-900/60 border-slate-700" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs text-muted-foreground">Host</Label>
                  <Input value={form.host} onChange={(e) => setForm({ ...form, host: e.target.value })} data-testid="webinar-host" placeholder="Founder" className="mt-1 bg-slate-900/60 border-slate-700" /></div>
                <div><Label className="text-xs text-muted-foreground">Join URL</Label>
                  <Input value={form.join_url} onChange={(e) => setForm({ ...form, join_url: e.target.value })} placeholder="https://zoom.us/j/..." className="mt-1 bg-slate-900/60 border-slate-700" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs text-muted-foreground">Meeting ID</Label>
                  <Input value={form.meeting_id} onChange={(e) => setForm({ ...form, meeting_id: e.target.value })} className="mt-1 bg-slate-900/60 border-slate-700" /></div>
                <div><Label className="text-xs text-muted-foreground">Passcode</Label>
                  <Input value={form.passcode} onChange={(e) => setForm({ ...form, passcode: e.target.value })} className="mt-1 bg-slate-900/60 border-slate-700" /></div>
              </div>
            </div>
            <DialogFooter>
              <Button onClick={create} disabled={busy} data-testid="save-webinar" className="bg-primary text-primary-foreground">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create Webinar"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
        <KPI label="Total" value={ov?.total_webinars} icon={Video} testid="kpi-total" />
        <KPI label="Upcoming" value={ov?.upcoming_count} icon={Calendar} accent="text-blue-400" testid="kpi-upcoming" />
        <KPI label="Completed" value={ov?.completed} icon={CheckCircle2} accent="text-emerald-400" testid="kpi-completed" />
        <KPI label="Registered" value={ov?.registered} icon={Users} accent="text-purple-400" testid="kpi-registered" />
        <KPI label="Confirmed" value={ov?.confirmed} icon={UserCheck} accent="text-emerald-400" testid="kpi-confirmed" />
        <KPI label="Attended" value={ov?.attended} icon={Radio} accent="text-primary" testid="kpi-attended" />
      </div>

      <div className="glass rounded-xl overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-800 font-semibold text-sm">All Webinars</div>
        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
        ) : items.length === 0 ? (
          <div className="text-center py-16 text-muted-foreground text-sm">No webinars yet. Create your first webinar to start registering leads.</div>
        ) : (
          <div className="divide-y divide-slate-800">
            {items.map((w) => (
              <button key={w.id} data-testid={`webinar-row-${w.id}`} onClick={() => navigate(`/admin/webinars/${w.id}`)}
                className="w-full flex items-center justify-between gap-3 px-5 py-4 text-left hover:bg-slate-800/40 transition-colors">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold truncate">{w.name}</span>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full border font-semibold ${STATUS_STYLES[w.status] || STATUS_STYLES.SCHEDULED}`}>{w.status}</span>
                  </div>
                  <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground flex-wrap">
                    <span className="flex items-center gap-1"><Calendar className="h-3 w-3" />{w.date}</span>
                    {w.start_time && <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{w.start_time}{w.end_time ? `–${w.end_time}` : ""}</span>}
                    {w.host && <span className="flex items-center gap-1"><UserCheck className="h-3 w-3" />{w.host}</span>}
                  </div>
                </div>
                <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
